import { define, getHost, html, inject, onCleanup, prop, useField } from '@vielzeug/ore';
import { computed } from '@vielzeug/ripple';
import { createCheckable, lifecycleSignal } from '../../core';
import type { CheckableProps, ComponentSize, ThemeColor } from '../../types';
import '../../content/icon/icon';
import { CONTROL_SIZE_PRESET, disablableBundle, sizableBundle, themableBundle } from '../../shared';
import {
  coarsePointerMixin,
  colorThemeMixin,
  disabledStateMixin,
  forcedColorsFormControlMixin,
  sizeVariantMixin,
} from '../../styles';
import { CHECKBOX_GROUP_CTX } from '../checkbox-group/checkbox-group';
import { applyCheckableBinding } from '../shared/field-binding';
import { defineFieldChecked, dispatchNativeFieldEvent, setFieldChecked } from '../shared/native-field-event';
import { renderHelperRegion } from '../shared/templates';
import componentStyles from './checkbox.css?inline';

export type OreCheckboxEvents = {
  change: Event;
  input: Event;
};

export type OreCheckboxProps = CheckableProps & {
  /** Theme color */
  color?: ThemeColor;
  /** Disable interaction */
  disabled?: boolean;
  /** Error message (marks field as invalid) */
  error?: string;
  /** Helper text displayed below the checkbox */
  helper?: string;
  /** Indeterminate state (partially checked) */
  indeterminate?: boolean;
  /** Require this checkbox to be checked for native form validation (e.g. a consent checkbox) */
  required?: boolean;
  /** Component size */
  size?: ComponentSize;
};

/**
 * A customizable checkbox component with theme colors, sizes, and indeterminate state support.
 *
 * @element ore-checkbox
 *
 * @attr {boolean} checked - Checked state
 * @attr {boolean} disabled - Disable checkbox interaction
 * @attr {boolean} indeterminate - Indeterminate (partially checked) state
 * @attr {boolean} required - Require this checkbox to be checked for native form validation
 * @attr {string} value - Field value submitted with forms
 * @attr {string} name - Form field name
 * @attr {string} color - Theme color: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'
 * @attr {string} size - Checkbox size: 'sm' | 'md' | 'lg'
 * @attr {string} error - Error message (marks field as invalid)
 * @attr {string} helper - Helper text displayed below the checkbox
 *
 * @fires input - Emitted when checkbox is toggled.
 * @fires change - Emitted when checkbox is toggled.
 *
 * @slot - Checkbox label text
 *
 * @cssprop --checkbox-size - Control size (width and height)
 * @cssprop --checkbox-radius - Control border radius
 * @cssprop --checkbox-bg - Unchecked background color
 * @cssprop --checkbox-border-color - Unchecked border color
 * @cssprop --checkbox-checked-bg - Checked/indeterminate background color
 * @cssprop --checkbox-color - Checkmark icon color
 * @cssprop --checkbox-font-size - Label font size
 * @part checkbox - The checkbox wrapper element
 * @part box - The visual checkbox box
 * @part label - The label element
 * @part helper-text - The helper/error text element
 *
 * @example
 * ```html
 * <ore-checkbox name="agree" required>I agree to the terms</ore-checkbox>
 * <ore-checkbox checked color="primary" size="lg">Enabled by default</ore-checkbox>
 * <ore-checkbox error="This field is required" helper="Check to continue">Accept</ore-checkbox>
 * ```
 */
export const CHECKBOX_TAG = 'ore-checkbox' as const;
define<OreCheckboxProps>(CHECKBOX_TAG, {
  formAssociated: true,
  props: {
    ...themableBundle,
    ...sizableBundle,
    ...disablableBundle,
    checked: prop.bool(false),
    error: prop.string(),
    helper: prop.string(),
    indeterminate: prop.bool(false),
    name: prop.string(),
    required: prop.bool(false),
    value: prop.string('on'),
  },
  setup(props) {
    const el = getHost();

    const groupCtx = inject(CHECKBOX_GROUP_CTX);

    const checkable = createCheckable({
      checked: props.checked,
      clearIndeterminateFirst: true,
      disabled: computed(() => props.disabled.value || Boolean(groupCtx?.disabled.value)),
      error: props.error,
      group: groupCtx,
      helper: props.helper,
      indeterminate: props.indeterminate,
      onToggle: (payload) => {
        checkable.triggerValidation('change');
        setFieldChecked(el, payload.checked);

        // In a checkbox-group, the group owns change emission/state updates.
        // Emitting here would bubble to the group and toggle a second time.
        if (groupCtx) return;

        dispatchNativeFieldEvent(el, 'input');
        dispatchNativeFieldEvent(el, 'change');
      },
      prefix: 'checkbox',
      required: props.required,
      signal: lifecycleSignal(onCleanup),
      value: props.value,
    });

    checkable.attachFormField(
      useField<string | null>({
        disabled: checkable.disabled,
        onReset: checkable.reset,
        toFormValue: (v) => v,
        validationMessage: checkable.validationMessage,
        validity: checkable.validity,
        value: checkable.checkableFormValue,
      }),
    );

    const {
      assistiveId,
      checked,
      disabled,
      errorText,
      handleClick,
      handleKeydown,
      helperText,
      indeterminate,
      labelId,
    } = checkable;

    defineFieldChecked(
      el,
      () => checked.value,
      (value) => {
        checked.value = value;
      },
    );

    applyCheckableBinding(
      props.size,
      {
        assistiveId,
        checked,
        disabled,
        errorText,
        handleClick,
        handleKeydown,
        helperText,
        indeterminate,
        labelId,
        required: props.required,
      },
      'checkbox',
    );

    return html`
      <div class="checkbox-wrapper" part="checkbox">
        <div class="box" part="box">
          <ore-icon class="checkmark" name="check" size="14" stroke-width="2" aria-hidden="true"></ore-icon>
          <ore-icon class="dash" name="minus" size="14" stroke-width="2" aria-hidden="true"></ore-icon>
        </div>
      </div>
      <span class="label" part="label" id="${labelId}"><slot></slot></span>
      ${renderHelperRegion(assistiveId, errorText, helperText)}
    `;
  },
  styles: [
    colorThemeMixin,
    forcedColorsFormControlMixin,
    disabledStateMixin,
    coarsePointerMixin,
    sizeVariantMixin(CONTROL_SIZE_PRESET),
    componentStyles,
  ],
});
